import api from './api';
import type {
  ColaboradorAtestadoContract,
  UploadAtestadoPayloadContract,
  DecisaoAbonoPayloadContract,
  SaudeTimelineItemContract,
  DashboardSaudeCorporativaContract,
  EmpresaSaudeDetalhesAdminContract,
  IndividuoSaudeClinicaAdminContract,
  EmpresaRepresentanteAtestadoContract,
  SalvarRepresentanteAtestadoContract,
} from '@sstexames/shared-types';

export interface ListarAtestadosFiltros {
  empresa_id?: number;
  colaborador_id?: number;
  status_administracao?: string;
  status_autenticidade?: string;
  busca?: string;
}

export const saudeService = {
  /**
   * Envia e cadastra um novo atestado médico
   */
  async uploadAtestado(
    payload: UploadAtestadoPayloadContract,
  ): Promise<ColaboradorAtestadoContract> {
    const res = await api.post<ColaboradorAtestadoContract>(
      '/saude/atestados/upload',
      payload,
    );
    return res.data;
  },

  /**
   * Upload direto de um arquivo de atestado (leitura por IA)
   */
  async uploadArquivo(
    file: File,
    empresaId: number,
    colaboradorId?: number,
  ): Promise<ColaboradorAtestadoContract> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('empresa_id', String(empresaId));
    if (colaboradorId) {
      formData.append('colaborador_id', String(colaboradorId));
    }
    const res = await api.post<ColaboradorAtestadoContract>(
      '/saude/atestados/upload-arquivo',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return res.data;
  },

  /**
   * Upload em lote de múltiplos arquivos de atestado (leitura por IA)
   */
  async uploadLote(
    files: File[],
    empresaId: number,
    colaboradorId?: number,
  ): Promise<{ total: number; atestados: ColaboradorAtestadoContract[] }> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('empresa_id', String(empresaId));
    if (colaboradorId) {
      formData.append('colaborador_id', String(colaboradorId));
    }
    const res = await api.post<{ total: number; atestados: ColaboradorAtestadoContract[] }>(
      '/saude/atestados/upload-lote',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return res.data;
  },

  /**
   * Registra a decisão administrativa de abono do RH
   */
  async decidirAbono(
    payload: DecisaoAbonoPayloadContract,
  ): Promise<ColaboradorAtestadoContract> {
    const res = await api.post<ColaboradorAtestadoContract>(
      '/saude/atestados/decisao-abono',
      payload,
    );
    return res.data;
  },

  /**
   * Lista atestados com filtros de empresa, colaborador, status e busca
   */
  async listarAtestados(
    filtros: ListarAtestadosFiltros = {},
  ): Promise<ColaboradorAtestadoContract[]> {
    const res = await api.get<ColaboradorAtestadoContract[]>('/saude/atestados', {
      params: filtros,
    });
    return res.data;
  },

  /**
   * Obtém detalhes de um atestado específico
   */
  async obterAtestado(id: number): Promise<ColaboradorAtestadoContract> {
    const res = await api.get<ColaboradorAtestadoContract>(`/saude/atestados/${id}`);
    return res.data;
  },

  /**
   * Obtém a linha do tempo unificada de saúde (exames + atestados) do colaborador
   */
  async obterTimelineColaborador(
    colaboradorId: number,
  ): Promise<SaudeTimelineItemContract[]> {
    const res = await api.get<SaudeTimelineItemContract[]>(
      `/saude/colaborador/${colaboradorId}/timeline`,
    );
    return res.data;
  },

  /**
   * Obtém o dashboard executivo de saúde corporativa e absenteísmo da empresa
   */
  async obterDashboardSaudeEmpresa(
    empresaId: number,
    periodo?: string,
  ): Promise<DashboardSaudeCorporativaContract> {
    const res = await api.get<DashboardSaudeCorporativaContract>(
      `/saude/empresa/${empresaId}/dashboard`,
      {
        params: periodo ? { periodo } : undefined,
      },
    );
    return res.data;
  },

  /**
   * Obtém os detalhes estratégicos de saúde corporativa, setores e conclusões IA 5W2H
   */
  async obterEmpresaDetalhes(
    empresaId: number,
  ): Promise<EmpresaSaudeDetalhesAdminContract> {
    const res = await api.get<EmpresaSaudeDetalhesAdminContract>(
      `/saude/empresa/${empresaId}/detalhes`,
    );
    return res.data;
  },

  /**
   * Obtém os dados clínicos completos e mapa anatômico 360 do colaborador
   */
  async obterIndividuoClinica(
    colaboradorId: number,
  ): Promise<IndividuoSaudeClinicaAdminContract> {
    const res = await api.get<IndividuoSaudeClinicaAdminContract>(
      `/saude/individuo/${colaboradorId}/clinica`,
    );
    return res.data;
  },

  /**
   * Dispara solicitação de reprocessamento com IA de um atestado existente
   */
  async reprocessarAtestado(
    atestadoId: number,
  ): Promise<ColaboradorAtestadoContract> {
    const res = await api.post<ColaboradorAtestadoContract>(
      `/saude/atestados/${atestadoId}/reprocessar`,
    );
    return res.data;
  },

  /**
   * Lista representantes cadastrados para assinatura de ofícios de atestados
   */
  async obterRepresentantes(
    empresaId?: number,
  ): Promise<EmpresaRepresentanteAtestadoContract[]> {
    const res = await api.get<EmpresaRepresentanteAtestadoContract[]>(
      '/saude/representantes',
      {
        params: empresaId ? { empresa_id: empresaId } : undefined,
      },
    );
    return res.data;
  },

  /**
   * Cadastra ou atualiza um representante para ofícios de atestados
   */
  async salvarRepresentante(
    payload: SalvarRepresentanteAtestadoContract,
  ): Promise<EmpresaRepresentanteAtestadoContract> {
    const res = await api.post<EmpresaRepresentanteAtestadoContract>(
      '/saude/representantes',
      payload,
    );
    return res.data;
  },

  /**
   * Remove um representante configurado
   */
  async excluirRepresentante(
    id: number,
    empresaId?: number,
  ): Promise<{ success: boolean }> {
    const res = await api.delete<{ success: boolean }>(
      `/saude/representantes/${id}`,
      {
        params: empresaId ? { empresa_id: empresaId } : undefined,
      },
    );
    return res.data;
  },

  /**
   * Define um representante como padrão da empresa
   */
  async definirRepresentantePadrao(
    id: number,
    empresaId?: number,
  ): Promise<EmpresaRepresentanteAtestadoContract> {
    const res = await api.patch<EmpresaRepresentanteAtestadoContract>(
      `/saude/representantes/${id}/padrao`,
      {},
      {
        params: empresaId ? { empresa_id: empresaId } : undefined,
      },
    );
    return res.data;
  },
};

